import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const TRANSLATED_ATTRIBUTES = ["aria-label", "placeholder", "title"] as const;
const SKIP_SELECTOR = "code, pre, kbd, samp, script, style, [data-i18n-skip]";
const sourceText = new WeakMap<Text, string>();
const sourceAttributes = new WeakMap<Element, Map<string, string>>();

type ResourceTree = { [key: string]: string | ResourceTree };

interface PhrasePattern {
  key: string;
  placeholders: string[];
  regex: RegExp;
  staticText: string;
}

function normalizePhrase(value: string) {
  return value.replace(/\s+/g, " ").trim();
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function literalPattern(literal: string) {
  // Keep the whitespace around placeholders: "Page {{page}}" must not match
  // "Pages", and "{{a}} of {{b}}" must not capture the spaces into a value.
  return escapeRegExp(literal.replace(/\s+/g, " ")).replace(/ /g, "\\s+");
}

function compilePattern(value: string, key: string): PhrasePattern | null {
  const placeholders: string[] = [];
  const literals: string[] = [];
  let cursor = 0;
  const matcher = /{{\s*([^},]+)(?:,[^}]*)?\s*}}/g;
  let match = matcher.exec(value);
  while (match) {
    literals.push(value.slice(cursor, match.index));
    placeholders.push(match[1].trim());
    cursor = match.index + match[0].length;
    match = matcher.exec(value);
  }
  if (!placeholders.length) return null;
  literals.push(value.slice(cursor));
  literals[0] = literals[0].trimStart();
  literals[literals.length - 1] = literals[literals.length - 1].trimEnd();
  const source = literals.map(literalPattern).join("(.+?)");
  return {
    key,
    placeholders,
    regex: new RegExp(`^${source}$`, "s"),
    staticText: normalizePhrase(literals.join("")),
  };
}

function collectPhrases(
  tree: ResourceTree,
  prefix: string,
  phrases: Map<string, string>,
  patterns: PhrasePattern[],
) {
  for (const [segment, value] of Object.entries(tree)) {
    const key = prefix ? `${prefix}.${segment}` : segment;
    if (typeof value === "string") {
      const pattern = compilePattern(value, key);
      if (pattern) {
        // Punctuation-only glue such as "{{value1}}: {{value2}}" has nothing
        // to translate, but would re-join live values ("09:14" -> "09: 14").
        if (/\p{L}/u.test(pattern.staticText)) patterns.push(pattern);
      } else {
        const normalized = normalizePhrase(value);
        // Hand-reviewed shared keys appear before the generated page catalog
        // and intentionally win when the same phrase occurs in both.
        if (normalized && !phrases.has(normalized)) phrases.set(normalized, key);
      }
    } else {
      collectPhrases(value, key, phrases, patterns);
    }
  }
}

/**
 * Translates static copy in older lazy-loaded screens through the same
 * semantic resource keys as newly migrated components. The bridge only
 * touches exact registered phrases, so names, ids, balances, and server
 * content are never guessed or machine-translated.
 */
export function LocalizedContentBridge() {
  const { i18n } = useTranslation();

  useEffect(() => {
    const root = document.body;
    const locale = i18n.resolvedLanguage === "th" ? "th" : "en";
    const phrases = new Map<string, string>();
    const patterns: PhrasePattern[] = [];
    const bundle = i18n.getResourceBundle("en", "translation") as
      | ResourceTree
      | undefined;
    if (bundle) collectPhrases(bundle, "", phrases, patterns);
    patterns.sort(
      (left, right) => right.staticText.length - left.staticText.length,
    );

    const translateValue = (value: string): string => {
      const match = /^(\s*)(.*?)(\s*)$/s.exec(value);
      if (!match) return value;
      const normalized = normalizePhrase(match[2]);
      const key = phrases.get(normalized);
      if (key) return `${match[1]}${i18n.t(key)}${match[3]}`;

      for (const pattern of patterns) {
        const values = pattern.regex.exec(normalized);
        if (!values) continue;
        const interpolation = Object.fromEntries(
          pattern.placeholders.map((placeholder, index) => [
            placeholder,
            values[index + 1],
          ]),
        );
        return `${match[1]}${i18n.t(pattern.key, interpolation)}${match[3]}`;
      }
      return value;
    };

    const translateText = (node: Text) => {
      if (node.parentElement?.closest(SKIP_SELECTOR)) return;
      const current = node.nodeValue ?? "";
      const stored = sourceText.get(node);
      if (locale === "en") {
        if (stored !== undefined) {
          if (current !== stored) node.nodeValue = stored;
          sourceText.delete(node);
        }
        return;
      }

      let source = stored ?? current;
      if (stored !== undefined) {
        const previousTranslation = translateValue(stored);
        // React may reuse a text node while replacing its server-backed value.
        // If it no longer contains our translation, treat the new value as the
        // source rather than restoring stale copy over live data.
        if (current !== stored && current !== previousTranslation) source = current;
      }
      const translated = translateValue(source);
      if (translated !== source) {
        sourceText.set(node, source);
        if (translated !== current) node.nodeValue = translated;
      }
    };

    const translateElement = (element: Element) => {
      if (element.closest(SKIP_SELECTOR)) return;
      const stored = sourceAttributes.get(element) ?? new Map<string, string>();
      for (const attribute of TRANSLATED_ATTRIBUTES) {
        const current = element.getAttribute(attribute);
        if (current === null) continue;
        const original = stored.get(attribute);
        if (locale === "en") {
          if (original !== undefined) element.setAttribute(attribute, original);
          stored.delete(attribute);
          continue;
        }

        let source = original ?? current;
        if (original !== undefined) {
          const previousTranslation = translateValue(original);
          if (current !== original && current !== previousTranslation) source = current;
        }
        const translated = translateValue(source);
        if (translated !== source) {
          stored.set(attribute, source);
          if (translated !== current) element.setAttribute(attribute, translated);
        }
      }
      if (stored.size) sourceAttributes.set(element, stored);
      else sourceAttributes.delete(element);
    };

    const translateTree = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        translateText(node as Text);
        return;
      }
      if (!(node instanceof Element)) return;
      translateElement(node);
      const walker = document.createTreeWalker(
        node,
        NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
      );
      let current = walker.nextNode();
      while (current) {
        if (current.nodeType === Node.TEXT_NODE) {
          translateText(current as Text);
        } else if (current instanceof Element) {
          translateElement(current);
        }
        current = walker.nextNode();
      }
    };

    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        translateTree(root);
      });
    });

    translateTree(root);
    observer.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: [...TRANSLATED_ATTRIBUTES],
    });
    return () => observer.disconnect();
  }, [i18n, i18n.resolvedLanguage]);

  return null;
}
